import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Plus, ChevronRight, Trash2, Edit2, X, Shield, Share2, DollarSign, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const HORSE_AVATARS = [
  "https://images.unsplash.com/photo-1769788233370-5d7125270cd5?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  "https://images.unsplash.com/photo-1717390702104-9c8a5e6edec2?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  "https://images.unsplash.com/photo-1760860507309-1c30f454d367?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
];

function HorsePhoto({ horse, index, authHeaders, API }) {
  const [photoUrl, setPhotoUrl] = useState(null);
  useEffect(() => {
    if (horse.photo_path) {
      axios.get(`${API}/files/${horse.photo_path}`, { ...authHeaders(), responseType: 'blob' })
        .then(res => setPhotoUrl(URL.createObjectURL(res.data)))
        .catch(() => {});
    }
    return () => { if (photoUrl) URL.revokeObjectURL(photoUrl); };
  }, [horse.photo_path]); // eslint-disable-line
  return (
    <img
      src={photoUrl || HORSE_AVATARS[index % HORSE_AVATARS.length]}
      alt={horse.name}
      className="w-full h-full object-cover"
      data-testid={`horse-photo-${horse.id}`}
    />
  );
}

const REGISTRATION_ASSOCIATIONS = [
  '', 'AQHA', 'APHA', 'AHA', 'Jockey Club', 'ApHC', 'ASHA', 'TWHBEA', 'MFTHBA', 'PHBA', 'USEF', 'USDF', 'Other'
];

function computeAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob + 'T00:00:00');
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
  return Math.max(0, age);
}

const HEALTH_COLORS = {
  green: { bg: '#D1FAE5', text: '#065F46', label: 'Healthy', dot: '#10B981' },
  yellow: { bg: '#FEF3C7', text: '#92400E', label: 'Attention', dot: '#F59E0B' },
  red: { bg: '#FEE2E2', text: '#991B1B', label: 'Needs Care', dot: '#EF4444' },
};

function HealthBadge({ score }) {
  if (!score) return null;
  const c = HEALTH_COLORS[score.status] || HEALTH_COLORS.green;
  const totalItems = (score.issues?.length || 0) + (score.warnings?.length || 0);
  return (
    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: c.bg, color: c.text }} data-testid={`health-badge-${score.status}`}>
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.dot }} />
      {c.label}{totalItems > 0 ? ` (${totalItems})` : ''}
    </div>
  );
}

export default function DashboardPage() {
  const { authHeaders, API, user, hasFullAccess } = useAuth();
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editHorse, setEditHorse] = useState(null);
  const [form, setForm] = useState({ name: '', breed: '', color: '', date_of_birth: '', gender: '', weight: '', notes: '', registration_association: '', registration_number: '', feed_schedule: [], turnout_schedule: '', special_alerts: '' });
  const [showHerdExpense, setShowHerdExpense] = useState(false);
  const [herdForm, setHerdForm] = useState({ date: '', category: '', description: '', amount: '', notes: '', horse_ids: [], split_type: 'equal' });
  const [savingHerd, setSavingHerd] = useState(false);

  const fetchHorses = async () => {
    try {
      const res = await axios.get(`${API}/horses`, authHeaders());
      setHorses(res.data);
    } catch (err) {
      toast.error('Failed to load horses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHorses(); }, []); // eslint-disable-line

  const handleSave = async (e) => {
    e.preventDefault();
    const data = {
      ...form,
      weight: form.weight ? parseFloat(form.weight) : null,
    };
    try {
      if (editHorse) {
        await axios.put(`${API}/horses/${editHorse.id}`, data, authHeaders());
        toast.success('Horse updated!');
      } else {
        await axios.post(`${API}/horses`, data, authHeaders());
        toast.success('Horse added!');
      }
      setShowAddDialog(false);
      setEditHorse(null);
      setForm({ name: '', breed: '', color: '', date_of_birth: '', gender: '', weight: '', notes: '', registration_association: '', registration_number: '', feed_schedule: [], turnout_schedule: '', special_alerts: '' });
      fetchHorses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (horseId, horseName) => {
    if (!window.confirm(`Delete ${horseName} and all records?`)) return;
    try {
      await axios.delete(`${API}/horses/${horseId}`, authHeaders());
      toast.success('Horse deleted');
      fetchHorses();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openEdit = (horse) => {
    setEditHorse(horse);
    setForm({
      name: horse.name, breed: horse.breed || '', color: horse.color || '',
      date_of_birth: horse.date_of_birth || '', gender: horse.gender || '',
      weight: horse.weight?.toString() || '', notes: horse.notes || '',
      registration_association: horse.registration_association || '',
      registration_number: horse.registration_number || '',
      feed_schedule: horse.feed_schedule || [],
      turnout_schedule: horse.turnout_schedule || '',
      special_alerts: horse.special_alerts || ''
    });
    setShowAddDialog(true);
  };

  const openAdd = () => {
    if (!hasFullAccess) {
      toast.error('Subscribe to add horses and manage records.');
      return;
    }
    setEditHorse(null);
    setForm({ name: '', breed: '', color: '', date_of_birth: '', gender: '', weight: '', notes: '', registration_association: '', registration_number: '', feed_schedule: [], turnout_schedule: '', special_alerts: '' });
    setShowAddDialog(true);
  };

  const handleHerdExpense = async (e) => {
    e.preventDefault();
    setSavingHerd(true);
    try {
      await axios.post(`${API}/expenses/shared`, {
        ...herdForm,
        amount: parseFloat(herdForm.amount) || 0,
      }, authHeaders());
      toast.success('Herd expense logged for ' + (herdForm.horse_ids.length === 0 ? 'all horses' : `${herdForm.horse_ids.length} horses`) + '!');
      setShowHerdExpense(false);
      setHerdForm({ date: '', category: '', description: '', amount: '', notes: '', horse_ids: [], split_type: 'equal' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log herd expense');
    } finally {
      setSavingHerd(false);
    }
  };

  const toggleHerdHorse = (id) => {
    setHerdForm(prev => ({
      ...prev,
      horse_ids: prev.horse_ids.includes(id) ? prev.horse_ids.filter(h => h !== id) : [...prev.horse_ids, id]
    }));
  };

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 animate-fade-in">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332]" data-testid="dashboard-title">
              Your Horses
            </h1>
            <p className="text-[#4B6B58] mt-1">Welcome back, {user?.name}. Manage your equine companions.</p>
          </div>
          <div className="flex gap-3">
            {hasFullAccess && horses.length > 1 && (
              <button
                onClick={() => setShowHerdExpense(true)}
                data-testid="herd-expense-button"
                className="flex items-center gap-2 px-5 py-3 rounded-full border-2 border-[#1B4332] text-[#1B4332] font-semibold btn-lift hover:bg-[#1B4332] hover:text-white"
              >
                <DollarSign size={18} /> Herd Expense
              </button>
            )}
            <button
              onClick={openAdd}
              data-testid="add-horse-button"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F]"
            >
              <Plus size={18} /> Add Horse
            </button>
          </div>
        </div>

        {/* Horse Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-[2rem] h-64 animate-pulse shadow-[0_8px_30px_rgb(27,67,50,0.06)]"></div>
            ))}
          </div>
        ) : horses.length === 0 ? (
          <div className="bg-white rounded-[2rem] p-12 text-center shadow-[0_8px_30px_rgb(27,67,50,0.06)]" data-testid="no-horses-message">
            <div className="w-20 h-20 rounded-full bg-[#E9EFE8] flex items-center justify-center mx-auto mb-6">
              <Plus size={32} className="text-[#4B6B58]" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] mb-2">No Horses Yet</h3>
            <p className="text-[#4B6B58] mb-6">Add your first horse to start tracking health records.</p>
            <button
              onClick={openAdd}
              data-testid="add-first-horse-button"
              className="px-8 py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F]"
            >
              Add Your First Horse
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {horses.map((horse, i) => (
              <div
                key={horse.id}
                className={`bg-white rounded-[2rem] overflow-hidden card-hover shadow-[0_8px_30px_rgb(27,67,50,0.06)] animate-fade-in ${horse.is_shared ? 'ring-2 ring-[#7C3AED]/20' : ''}`}
                data-testid={`horse-card-${horse.id}`}
              >
                <div className="h-40 overflow-hidden relative">
                  <HorsePhoto horse={horse} index={i} authHeaders={authHeaders} API={API} />
                  {horse.is_shared && (
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#7C3AED]/90 text-white text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm shadow-md" data-testid={`shared-badge-${horse.id}`}>
                      <Share2 size={10} /> Shared by {horse.shared_by || 'Owner'}
                    </div>
                  )}
                  {!horse.is_shared && (
                    <div className="absolute top-3 right-3 flex gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); openEdit(horse); }}
                        data-testid={`edit-horse-${horse.id}`}
                        className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-[#4B6B58] hover:text-[#1B4332] btn-lift"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(horse.id, horse.name); }}
                        data-testid={`delete-horse-${horse.id}`}
                        className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-red-400 hover:text-red-600 btn-lift"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
                <Link to={`/horse/${horse.id}`} className="block p-6" data-testid={`horse-link-${horse.id}`}>
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332]">{horse.name}</h3>
                    <HealthBadge score={horse.health_score} />
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {horse.breed && <span className="px-3 py-1 rounded-full bg-[#E9EFE8] text-xs text-[#4B6B58] font-medium">{horse.breed}</span>}
                    {horse.color && <span className="px-3 py-1 rounded-full bg-[#F9F7F3] text-xs text-[#4B6B58] font-medium">{horse.color}</span>}
                    {horse.date_of_birth && <span className="px-3 py-1 rounded-full bg-[#E9EFE8] text-xs text-[#4B6B58] font-medium">{computeAge(horse.date_of_birth)} yrs</span>}
                    {horse.registration_association && <span className="px-3 py-1 rounded-full bg-[#F9F7F3] text-xs text-[#2D6A4F] font-semibold">{horse.registration_association}</span>}
                  </div>
                  {/* Health score details (issues/warnings) */}
                  {horse.health_score && (horse.health_score.issues?.length > 0 || horse.health_score.warnings?.length > 0) && (
                    <div className="mb-3 space-y-1" data-testid={`health-details-${horse.id}`}>
                      {(horse.health_score.issues || []).slice(0, 2).map((issue, idx) => (
                        <p key={idx} className="text-xs text-[#991B1B] flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-[#EF4444] flex-shrink-0" /> {issue}
                        </p>
                      ))}
                      {(horse.health_score.warnings || []).slice(0, 2).map((warn, idx) => (
                        <p key={idx} className="text-xs text-[#92400E] flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-[#F59E0B] flex-shrink-0" /> {warn}
                        </p>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-1 text-[#2D6A4F] text-sm font-medium">
                    View Records <ChevronRight size={14} />
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#F2F7F4] border-[#D1E0D5] rounded-[1.5rem] max-w-md" data-testid="horse-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">
              {editHorse ? 'Edit Horse' : 'Add New Horse'}
            </DialogTitle>
            <DialogDescription className="text-[#4B6B58]">
              {editHorse ? 'Update your horse\'s information.' : 'Enter your horse\'s details below.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-[#122A1F] mb-1">Name *</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="horse-name-input" className="stable-input w-full" placeholder="Horse name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#122A1F] mb-1">Breed</label>
                <input value={form.breed} onChange={e => setForm({...form, breed: e.target.value})} data-testid="horse-breed-input" className="stable-input w-full" placeholder="Breed" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#122A1F] mb-1">Color</label>
                <input value={form.color} onChange={e => setForm({...form, color: e.target.value})} data-testid="horse-color-input" className="stable-input w-full" placeholder="Color" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#122A1F] mb-1">Date of Birth</label>
                <input type="date" value={form.date_of_birth} onChange={e => setForm({...form, date_of_birth: e.target.value})} data-testid="horse-dob-input" className="stable-input w-full" />
                {form.date_of_birth && (
                  <span className="text-xs text-[#2D6A4F] mt-1 block">Age: {computeAge(form.date_of_birth)} years</span>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#122A1F] mb-1">Gender</label>
                <select value={form.gender} onChange={e => setForm({...form, gender: e.target.value})} data-testid="horse-gender-input" className="stable-input w-full">
                  <option value="">Select...</option>
                  <option value="Stallion">Stallion</option>
                  <option value="Mare">Mare</option>
                  <option value="Gelding">Gelding</option>
                  <option value="Colt">Colt</option>
                  <option value="Filly">Filly</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#122A1F] mb-1">Registration Association</label>
              <div className="grid grid-cols-2 gap-4">
                <select value={form.registration_association} onChange={e => setForm({...form, registration_association: e.target.value})} data-testid="horse-reg-assoc-input" className="stable-input w-full">
                  <option value="">None</option>
                  {REGISTRATION_ASSOCIATIONS.filter(a => a).map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <input value={form.registration_number} onChange={e => setForm({...form, registration_number: e.target.value})} data-testid="horse-reg-number-input" className="stable-input w-full" placeholder="Registration #" disabled={!form.registration_association} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#122A1F] mb-1">Weight (lbs)</label>
              <input type="number" value={form.weight} onChange={e => setForm({...form, weight: e.target.value})} data-testid="horse-weight-input" className="stable-input w-full" placeholder="lbs" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#122A1F] mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="horse-notes-input" className="stable-input w-full" rows={2} placeholder="Any additional notes..." />
            </div>

            {/* Barn Board Fields */}
            <div className="border-t border-[#D1E0D5] pt-4 mt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#4B6B58] mb-3">Barn Board Info</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#122A1F] mb-1">Turnout Schedule</label>
                  <input value={form.turnout_schedule} onChange={e => setForm({...form, turnout_schedule: e.target.value})} data-testid="horse-turnout-input" className="stable-input w-full" placeholder="e.g. AM paddock, PM pasture" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#122A1F] mb-1">Special Alerts</label>
                  <input value={form.special_alerts} onChange={e => setForm({...form, special_alerts: e.target.value})} data-testid="horse-alerts-input" className="stable-input w-full" placeholder="e.g. Cribber, easy keeper" />
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-[#122A1F] mb-1">Feed Schedule</label>
                {(form.feed_schedule || []).map((feed, i) => (
                  <div key={i} className="grid grid-cols-4 gap-2 mb-2">
                    <input value={feed.time || ''} onChange={e => { const fs = [...form.feed_schedule]; fs[i] = {...fs[i], time: e.target.value}; setForm({...form, feed_schedule: fs}); }} className="stable-input text-sm" placeholder="Time" data-testid={`feed-time-${i}`} />
                    <input value={feed.feed_type || ''} onChange={e => { const fs = [...form.feed_schedule]; fs[i] = {...fs[i], feed_type: e.target.value}; setForm({...form, feed_schedule: fs}); }} className="stable-input text-sm" placeholder="Feed type" data-testid={`feed-type-${i}`} />
                    <input value={feed.amount || ''} onChange={e => { const fs = [...form.feed_schedule]; fs[i] = {...fs[i], amount: e.target.value}; setForm({...form, feed_schedule: fs}); }} className="stable-input text-sm" placeholder="Amount" data-testid={`feed-amount-${i}`} />
                    <button type="button" onClick={() => { const fs = form.feed_schedule.filter((_, idx) => idx !== i); setForm({...form, feed_schedule: fs}); }} className="text-red-500 text-xs hover:text-red-700">Remove</button>
                  </div>
                ))}
                <button type="button" onClick={() => setForm({...form, feed_schedule: [...(form.feed_schedule || []), { time: '', feed_type: '', amount: '' }]})} data-testid="add-feed-entry" className="text-[#2D6A4F] text-xs font-medium hover:underline">+ Add feeding time</button>
              </div>
            </div>

            <button type="submit" data-testid="horse-save-button" className="w-full py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F]">
              {editHorse ? 'Update Horse' : 'Add Horse'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Herd Expense Dialog */}
      <Dialog open={showHerdExpense} onOpenChange={setShowHerdExpense}>
        <DialogContent className="bg-[#F2F7F4] border-[#D1E0D5] rounded-[1.5rem] max-w-lg" data-testid="herd-expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">Log Herd Expense</DialogTitle>
            <DialogDescription className="text-[#4B6B58]">Log an expense shared across multiple horses.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleHerdExpense} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Date *</label>
                <input type="date" value={herdForm.date} onChange={e => setHerdForm({...herdForm, date: e.target.value})} required data-testid="herd-date" className="stable-input w-full" />
              </div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Total Amount ($) *</label>
                <input type="number" step="0.01" value={herdForm.amount} onChange={e => setHerdForm({...herdForm, amount: e.target.value})} required data-testid="herd-amount" className="stable-input w-full" placeholder="0.00" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Category</label>
                <input value={herdForm.category} onChange={e => setHerdForm({...herdForm, category: e.target.value})} data-testid="herd-category" className="stable-input w-full" placeholder="e.g. Feed, Equipment" />
              </div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Split Type</label>
                <select value={herdForm.split_type} onChange={e => setHerdForm({...herdForm, split_type: e.target.value})} data-testid="herd-split" className="stable-input w-full">
                  <option value="equal">Split equally per horse</option>
                  <option value="full">Full amount per horse</option>
                </select>
              </div>
            </div>
            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Description</label>
              <input value={herdForm.description} onChange={e => setHerdForm({...herdForm, description: e.target.value})} data-testid="herd-desc" className="stable-input w-full" placeholder="e.g. 50lb bag of senior feed" />
            </div>

            {/* Horse Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[#122A1F]">Apply to horses</label>
                <button type="button" onClick={() => setHerdForm({...herdForm, horse_ids: []})} className="text-xs text-[#2D6A4F] font-medium hover:underline" data-testid="select-all-horses">
                  {herdForm.horse_ids.length === 0 ? 'All selected' : 'Select all'}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {horses.map(h => (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => toggleHerdHorse(h.id)}
                    data-testid={`herd-horse-${h.id}`}
                    className={`px-3 py-2 rounded-xl text-sm font-medium text-left transition-colors ${
                      herdForm.horse_ids.length === 0 || herdForm.horse_ids.includes(h.id)
                        ? 'bg-[#1B4332] text-white'
                        : 'bg-white text-[#4B6B58] border border-[#D1E0D5]'
                    }`}
                  >
                    {h.name}
                  </button>
                ))}
              </div>
              {herdForm.horse_ids.length === 0 && (
                <p className="text-xs text-[#2D6A4F] mt-1 font-medium">Applies to all {horses.length} horses</p>
              )}
              {herdForm.amount && (
                <p className="text-xs text-[#4B6B58] mt-1">
                  {herdForm.split_type === 'equal'
                    ? `$${(parseFloat(herdForm.amount) / (herdForm.horse_ids.length || horses.length)).toFixed(2)} per horse`
                    : `$${parseFloat(herdForm.amount).toFixed(2)} per horse (full amount)`
                  }
                </p>
              )}
            </div>

            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Notes</label>
              <textarea value={herdForm.notes} onChange={e => setHerdForm({...herdForm, notes: e.target.value})} data-testid="herd-notes" className="stable-input w-full" rows={2} />
            </div>
            <button type="submit" disabled={savingHerd} data-testid="herd-save" className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F] disabled:opacity-50">
              {savingHerd ? <Loader2 size={16} className="animate-spin" /> : <DollarSign size={16} />}
              {savingHerd ? 'Saving...' : 'Log Herd Expense'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
